<template>
    <BaseTable
        :columns="columnas"
        :data="props.eventos"
        :loading="props.loading"
        :skeleton="props.loading"
        :skeleton-rows="props.eventos.length || 5"
        :custom-class="props.refrescando ? 'tw:opacity-60 tw:transition-opacity' : ''"
        row-key="id"
        empty-text="No se han encontrado eventos con estos filtros."
        :expanded-row-keys="eventosAbiertos"
        data-cy="resumen-ventas-tabla"
    >
        <template #cell-codigo="{ row }">
            <span class="tw:font-semibold tw:text-slate-500">{{ row.codigo }}</span>
        </template>

        <template #cell-nombre="{ row }">
            <span :title="row.nombre_completo" class="tw:block tw:truncate tw:font-semibold tw:text-slate-800">
                {{ row.nombre_completo }}
            </span>
        </template>

        <template #cell-fecha="{ row }">
            <span class="tw:text-slate-600">{{ row.fecha_inicio }}</span>
        </template>

        <template #cell-teatro="{ row }">
            <span class="tw:flex tw:items-center tw:gap-1.5 tw:text-slate-600">
                <i class="fas fa-map-marker-alt tw:text-slate-400"></i>{{ row.teatro }}
            </span>
        </template>

        <template #cell-taquilla="{ row }">
            <CeldaCanal :canal="canalDe(row, 'taquilla')" />
        </template>

        <template #cell-online="{ row }">
            <CeldaCanal :canal="canalDe(row, 'online')" />
        </template>

        <template #cell-invitaciones="{ row }">
            <span class="tw:font-medium tw:text-slate-700">{{ canalDe(row, 'invitaciones').entradas }}</span>
        </template>

        <template #cell-total_entradas="{ row }">
            <span class="tw:font-bold tw:text-slate-800">{{ entradasFila(row) }}</span>
        </template>

        <template #cell-recaudacion="{ row }">
            <span class="tw:font-bold tw:text-blue-700">{{ importe(importeFila(row)) }}</span>
        </template>

        <template #cell-sesiones="{ row }">
            <Button
                variant="default"
                :custom-class="'tw:justify-center tw:whitespace-nowrap'"
                :data-cy="`resumen-ventas-ver-sesiones-${row.id}`"
                @click="props.detalle.alternarSesiones(row)"
            >
                {{ props.detalle.sesionesAbiertas(row.id) ? 'Ocultar' : 'Ver sesiones' }}
            </Button>
        </template>

        <!--
            Nivel 2: sesiones del evento. Cada sesión trae su propio estado de
            carga y su propio desglose (nivel 3) dentro del panel.
        -->
        <template #row-expanded="{ row }">
            <div class="tw:flex tw:flex-col">
                <div
                    v-if="props.detalle.estadoEvento(row.id).cargandoSesiones"
                    class="tw:bg-slate-50/70 tw:border-t tw:border-slate-200 tw:p-4"
                    data-cy="resumen-ventas-sesiones-skeleton"
                >
                    <div
                        v-for="n in 2"
                        :key="`resumen-ventas-sesion-sk-${n}`"
                        class="tw:flex tw:flex-col tw:gap-3 tw:lg:flex-row tw:lg:items-center tw:lg:justify-between"
                        :class="n > 1 ? 'tw:mt-3 tw:border-t tw:border-slate-200 tw:pt-3' : ''"
                    >
                        <!-- Pastilla de fecha + título de la sesión (el código
                             es opcional y no siempre se pinta: una sola barra). -->
                        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                            <SkeletonItem height="tw:h-7" customClass="tw:w-40 tw:rounded-full" />
                            <SkeletonItem height="tw:h-4" customClass="tw:w-48 tw:max-w-full tw:rounded" />
                        </div>
                        <!-- Caja de recaudación + botón Ventas -->
                        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                            <div class="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:px-3 tw:py-1">
                                <SkeletonItem height="tw:h-3" customClass="tw:w-28 tw:rounded" />
                                <SkeletonItem height="tw:h-5" customClass="tw:w-20 tw:rounded" />
                                <SkeletonItem height="tw:h-5" customClass="tw:w-24 tw:rounded" />
                            </div>
                            <SkeletonItem height="tw:h-9" customClass="tw:w-28 tw:rounded-sm" />
                        </div>
                    </div>
                </div>

                <div v-else-if="props.detalle.estadoEvento(row.id).sesionError" class="tw:flex tw:flex-col tw:items-start tw:gap-2 tw:p-4">
                    <span class="tw:text-sm tw:text-red-600">No se han podido cargar las sesiones de este evento.</span>
                    <Button variant="border-error" icon="fas fa-redo" @click="props.detalle.reintentarSesiones(row.id)">Reintentar</Button>
                </div>

                <p
                    v-else-if="!props.detalle.sesionesDe(row.id).length"
                    class="tw:m-0 tw:p-4 tw:text-sm tw:text-slate-500"
                >
                    Este evento no tiene sesiones en el rango de fechas seleccionado.
                </p>

                <SesionVentaPanel
                    v-for="sesion in props.detalle.sesionesDe(row.id)"
                    :key="`${row.id}-${sesion.id}`"
                    :sesion="sesion"
                    :estado="props.detalle.estadoDesglose(row.id, sesion)"
                    :abierto="props.detalle.desgloseAbierto(row.id, sesion)"
                    @toggle-ventas="props.detalle.alternarDesglose(row.id, $event)"
                    @reintentar-desglose="props.detalle.reintentarDesglose(row.id, $event)"
                />
            </div>
        </template>

        <!-- Fila de totales: sumas de la página cargada, no del rango completo. -->
        <template #footer>
            <tr class="tw:bg-slate-50 tw:border-t tw:border-slate-200 tw:font-semibold" data-cy="resumen-ventas-totales">
                <td colspan="2" class="tw:px-5 tw:py-4 tw:text-slate-700">
                    <i class="fas fa-calculator tw:mr-2 tw:text-blue-600"></i>
                    Totales de esta página ({{ props.totales.eventos }} {{ props.totales.eventos === 1 ? 'evento' : 'eventos' }})
                </td>
                <!-- Espejo de las columnas ocultas en pantallas chicas. -->
                <td class="tw:hidden tw:lg:table-cell"></td>
                <td class="tw:hidden tw:lg:table-cell"></td>
                <td class="tw:px-5 tw:py-4 tw:text-slate-700">
                    <CeldaCanal :canal="props.totales.taquilla" />
                </td>
                <td class="tw:px-5 tw:py-4 tw:text-slate-700">
                    <CeldaCanal :canal="props.totales.online" />
                </td>
                <td class="tw:px-5 tw:py-4 tw:text-center tw:text-slate-700">{{ props.totales.invitaciones.entradas }}</td>
                <td class="tw:px-5 tw:py-4 tw:text-center tw:text-slate-800">{{ props.totales.totalEntradas }}</td>
                <td class="tw:px-5 tw:py-4 tw:text-center tw:text-blue-700">{{ importe(props.totales.recaudacion) }}</td>
            </tr>
        </template>
    </BaseTable>
</template>

<script setup>
    import { computed } from 'vue';
    import appFilters from '@/filters';

    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import { SkeletonItem } from '@/modules/admin/ui/skeleton';
    import SesionVentaPanel from '@/modules/admin/eventos/resumen-ventas/components/SesionVentaPanel.vue';
    import CeldaCanal from '@/modules/admin/eventos/resumen-ventas/components/CeldaCanal.vue';
    import { canalDe, entradasFila, importeFila } from '@/composables/admin/eventos/resumenVentasAgregados';

    const props = defineProps({
        eventos: {
            type: Array,
            default: () => []
        },
        totales: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        refrescando: {
            type: Boolean,
            default: false
        },
        /**
         * Composable `useResumenVentasDetalle` completo. Se pasa como UNA prop
         * en vez de abrir diez props de callbacks: la tabla y el panel son
         * tontos de todas formas (sólo llaman a lo que les dan), y así el
         * drill-down sigue viviendo en un único sitio.
         */
        detalle: {
            type: Object,
            required: true
        }
    });

    const importe = appFilters.formatear_precio;

    const columnas = [
        { key: 'codigo', label: 'Código', field: 'codigo', centered: true, class: 'tw:w-20 tw:md:w-24' },
        { key: 'nombre', label: 'Nombre evento', field: 'nombre_completo', class: 'tw:w-32 tw:md:w-64' },
        // Las columnas de contexto (fecha y recinto) no entran en pantallas
        // chicas. Necesitan `tw:w-0` además de `tw:hidden`: BaseTable fija el
        // ancho por `colgroup` y una columna oculta sin ancho seguiría robando
        // sitio a las demás en `table-layout: fixed`.
        { key: 'fecha', label: 'Fecha', field: 'fecha_inicio', centered: true, class: 'tw:hidden tw:lg:table-cell tw:w-0 tw:lg:w-40' },
        { key: 'teatro', label: 'Nombre teatro', field: 'teatro', class: 'tw:hidden tw:lg:table-cell tw:w-0 tw:lg:w-40' },
        { key: 'taquilla', label: 'Ventas taquilla', centered: true, class: 'tw:w-32' },
        { key: 'online', label: 'Ventas online', centered: true, class: 'tw:w-32' },
        { key: 'invitaciones', label: 'Invitaciones', centered: true, class: 'tw:w-28' },
        { key: 'total_entradas', label: 'Total entradas', centered: true, class: 'tw:w-24 tw:md:w-28' },
        { key: 'recaudacion', label: 'Recaudación', centered: true, class: 'tw:w-28 tw:md:w-32' },
        { key: 'sesiones', label: 'Sesiones', centered: true, class: 'tw:w-32' }
    ];

    /**
     * Claves de los eventos con las sesiones desplegadas. Se leen dentro de un
     * computed para que el estado del composable (que vive en un ref) participe
     * de la reactividad del render.
     */
    const eventosAbiertos = computed(() =>
        Object.keys(props.detalle.eventos.value).filter(id => props.detalle.eventos.value[id].abierto)
    );
</script>
